import {
    EuiButtonEmpty,
    EuiCallOut,
    EuiFlexGroup,
    EuiFlexItem,
    EuiIcon,
    EuiPanel,
    EuiPopover,
    EuiSpacer,
    EuiText,
} from '@elastic/eui'
import { PropsWithChildren, useMemo, useState } from 'react'

import { Option } from '@services/options'
import { t } from 'i18next'

type InheritanceWarningWrapperProps = PropsWithChildren & {
    option: Option
}

const InheritanceWarningWrapper = ({
    option,
    children,
}: InheritanceWarningWrapperProps) => {
    const [isOpen, setIsOpen] = useState<boolean>(false)

    const panelContent = useMemo(
        () =>
            option.inheritanceSource ? (
                <>
                    <EuiText size="xs">
                        {t('options:inherited_option_disabled_info_1', {
                            inheritanceSource: option.inheritanceSource?.name,
                        })}
                    </EuiText>
                    <EuiSpacer size="xs" />
                    <EuiText size="xs">
                        {t('options:inherited_option_disabled_info_2')}
                    </EuiText>
                </>
            ) : (
                <>
                    <EuiText size="xs">
                        {t('options:inherited_option_undefined_1')}
                    </EuiText>
                    <EuiSpacer size="xs" />
                    <EuiText size="xs">
                        {t('options:inherited_option_undefined_2')}
                    </EuiText>
                </>
            ),
        [option.inheritanceSource]
    )

    const result = useMemo(() => {
        if (!option.isInherited || option.inheritanceSource?.isEnabled)
            return children

        return (
            <EuiCallOut
                title={
                    <EuiFlexGroup gutterSize="xs" alignItems="center">
                        <EuiFlexItem grow={false}>
                            <EuiIcon type={'warning'} />
                        </EuiFlexItem>
                        <EuiFlexItem>
                            <EuiPopover
                                isOpen={isOpen}
                                anchorPosition="upCenter"
                                closePopover={() => setIsOpen(false)}
                                button={
                                    <EuiButtonEmpty
                                        size="xs"
                                        iconType={'iInCircle'}
                                        iconSide="right"
                                        color="warning"
                                        onClick={() =>
                                            setIsOpen((prev) => !prev)
                                        }
                                    >
                                        {t(
                                            'options:inherited_option_disabled_title'
                                        )}
                                    </EuiButtonEmpty>
                                }
                            >
                                <EuiPanel
                                    style={{ maxWidth: '20vw' }}
                                    hasShadow={false}
                                    hasBorder={false}
                                    paddingSize="s"
                                >
                                    {panelContent}
                                </EuiPanel>
                            </EuiPopover>
                        </EuiFlexItem>
                    </EuiFlexGroup>
                }
                heading="h5"
                size="s"
                color="warning"
                style={{
                    padding: 0,
                    marginTop: 6,
                    marginBottom: 6,
                }}
            >
                {children}
            </EuiCallOut>
        )
    }, [
        option.isInherited,
        option.inheritanceSource?.isEnabled,
        panelContent,
        children,
        isOpen,
    ])

    return result
}

export default InheritanceWarningWrapper
